/**
 * What counts as "the thing you type in the username box".
 *
 * The login form has always supported a mobile number — the API's `/auth/login`
 * takes `email` OR `phone` — but the client only recognised a number when it
 * arrived as exactly ten bare digits. Nobody types a phone number that way.
 * `+91 98765 43210`, `98765-43210`, `098765 43210` all failed the test, fell
 * through to the email branch, found no `@`, and the Sign in button simply
 * stayed dead with nothing on screen explaining why.
 *
 * So: normalise the way a person writes a number down, then apply the API's own
 * rule to the result. The ten-digit shape is deliberately the SAME rule the
 * server enforces (`/^\d{10}$/`) — this file makes the input reach that rule,
 * it does not invent a looser one, because a client that accepts what the
 * server rejects is a worse failure than the one being fixed.
 *
 * There is no separate "username" to support. Accounts created without a real
 * inbox (students, drivers) are given a synthetic address like `ravi@mnr.in`,
 * so a username in this system already IS an email as far as login is
 * concerned, and `User` has no `username` column to match against.
 */

/** Everything a person might put between the digits, and nothing else. */
const PUNCTUATION = /[\s\-().]/g;

/**
 * Returns the bare 10-digit national number, or null if the input is not one.
 *
 * Accepts a leading `+91` / `91` / `0` — the country code and the old STD
 * trunk prefix are how numbers are written on ID cards, in address books and
 * by the phone's own autofill.
 */
export function normalizePhone(value: string): string | null {
  const bare = value.replace(PUNCTUATION, "");
  if (!bare) return null;

  // Strip the prefix people carry over from their contacts app.
  const national = bare
    .replace(/^\+91/, "")
    .replace(/^0091/, "")
    .replace(/^91(?=\d{10}$)/, "")
    .replace(/^0(?=\d{10}$)/, "");

  return /^\d{10}$/.test(national) ? national : null;
}

/**
 * True when the identifier should be sent as `phone` rather than `email`.
 *
 * An address is never a phone number even if it is all digits before the `@`,
 * so the `@` wins — `1234567890@school.edu` is an inbox.
 */
export function isPhoneNumber(value: string): boolean {
  if (value.includes("@")) return false;
  return normalizePhone(value) !== null;
}

/**
 * True when the identifier is complete enough to attempt a sign-in — a phone
 * number we can normalise, or something with an `@` in it.
 *
 * This gates the Sign in button, so it is intentionally permissive about the
 * email half: judging an address here only ever means refusing a real one that
 * a regex did not expect, and the server will say so far more accurately.
 */
export function isLoginIdentifier(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return isPhoneNumber(trimmed) || trimmed.includes("@");
}
